import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Key, Power, Trash } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Users() {
  const { user: me } = useAuth();
  const queryClient = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "staff" });
  const [creating, setCreating] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  const toggleActive = async (u) => {
    try {
      const { data } = await api.patch(`/users/${u.id}/status`, { active: u.active === false });
      toast.success(data.message);
      queryClient.invalidateQueries({ queryKey: ["users"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete user "${u.name}" (${u.email})? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const resetPw = async (e) => {
    e.preventDefault();
    setPwSaving(true);
    try {
      await api.patch(`/users/${pwUser.id}/password`, { password: pwValue });
      toast.success(`Password updated for ${pwUser.email}`);
      setPwUser(null);
      setPwValue("");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setPwSaving(false);
    }
  };

  const { data: users } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });
  const { data: roles } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => (await api.get("/roles")).data,
  });

  const changeRole = async (userId, role) => {
    try {
      await api.patch(`/users/${userId}/role`, { role });
      toast.success("Role updated");
      queryClient.invalidateQueries({ queryKey: ["users"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/users", newUser);
      toast.success(`User ${newUser.email} created`);
      setAddOpen(false);
      setNewUser({ name: "", email: "", password: "", role: "staff" });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter text-slate-900">Users</h1>
          <p className="mt-1 text-sm text-slate-500">Create users and assign roles to control what each person can do</p>
        </div>
        <Button onClick={() => setAddOpen(true)} data-testid="add-user-button" className="bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
          <Plus size={16} className="mr-1" /> Add User
        </Button>
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <Table data-testid="users-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Verified</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-44">Role</TableHead>
              <TableHead className="w-36">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(users || []).map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-semibold text-slate-900">{u.name}</TableCell>
                <TableCell className="text-slate-600">{u.email}</TableCell>
                <TableCell>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${u.verified ? "bg-[#ECFDF5] text-[#047857]" : "bg-[#FFFBEB] text-[#B45309]"}`}>
                    {u.verified ? "Verified" : "Pending"}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${u.active !== false ? "bg-[#ECFDF5] text-[#047857]" : "bg-red-50 text-red-700"}`} data-testid={`user-status-${u.id}`}>
                    {u.active !== false ? "Active" : "Deactivated"}
                  </span>
                </TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)} disabled={u.id === me?.id}>
                    <SelectTrigger data-testid={`user-role-select-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(roles || []).map((r) => (
                        <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <button title="Set new password" data-testid={`user-reset-pw-${u.id}`} onClick={() => { setPwUser(u); setPwValue(""); }}
                      className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700">
                      <Key size={16} />
                    </button>
                    <button title={u.active !== false ? "Deactivate user" : "Activate user"} data-testid={`user-toggle-active-${u.id}`} disabled={u.id === me?.id} onClick={() => toggleActive(u)}
                      className={`rounded-md p-1.5 transition-colors disabled:opacity-30 ${u.active !== false ? "text-slate-400 hover:bg-amber-50 hover:text-amber-600" : "text-emerald-500 hover:bg-emerald-50"}`}>
                      <Power size={16} />
                    </button>
                    <button title="Delete user" data-testid={`user-delete-${u.id}`} disabled={u.id === me?.id} onClick={() => removeUser(u)}
                      className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-30">
                      <Trash size={16} />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight">Add User</DialogTitle>
            <DialogDescription>The account is created pre-verified. Share the password with the user.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createUser} className="space-y-4" data-testid="add-user-form">
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input data-testid="add-user-name-input" required value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="add-user-email-input" type="email" required value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input data-testid="add-user-password-input" type="text" required minLength={6} value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Min. 6 characters" />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger data-testid="add-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(roles || []).map((r) => (
                    <SelectItem key={r.name} value={r.name}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={creating} data-testid="add-user-submit-button" className="bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
                {creating ? "Creating…" : "Create user"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwUser} onOpenChange={(v) => !v && setPwUser(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="admin-reset-pw-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold tracking-tight">Set New Password</DialogTitle>
            <DialogDescription>Set a new password for {pwUser?.email}. Share it with the user — they can change it after logging in.</DialogDescription>
          </DialogHeader>
          <form onSubmit={resetPw} className="space-y-4" data-testid="admin-reset-pw-form">
            <div className="space-y-1.5">
              <Label>New password</Label>
              <Input data-testid="admin-reset-pw-input" type="text" required minLength={6} value={pwValue} onChange={(e) => setPwValue(e.target.value)} placeholder="Min. 6 characters" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setPwUser(null)}>Cancel</Button>
              <Button type="submit" disabled={pwSaving} data-testid="admin-reset-pw-submit" className="bg-[#EA580C] font-semibold text-white hover:bg-[#C2410C]">
                {pwSaving ? "Updating…" : "Set password"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
